"use client"

import React, { useState, useEffect } from "react"
import { Plus, Search, Edit, Trash2, Eye, MapPin, Clock, Star, LayoutGrid, List } from "lucide-react"
import { Box, Flex, Grid, Card, Heading, Text, TextField, Select, Button, Badge, IconButton, Table, Avatar } from "@radix-ui/themes"
import { restaurantAPI } from "../services/api"
import RestaurantModal from "../components/RestaurantModal"
import RestaurantDetailsModal from "../components/RestaurantDetailsModal"
import PageHeader from "../components/ui/dashboard/PageHeader"
import LoadingSpinner from "../components/ui/dashboard/LoadingSpinner"

interface Restaurant {
  _id: string
  name: string
  description: string
  category: string
  address: {
    street: string
    city: string
    state: string
    zipCode: string
    coordinates: {
      latitude: number
      longitude: number
    }
  }
  contact?: {
    phone?: string
    email?: string
  }
  phone?: string
  email?: string
  image: string
  rating: {
    average: number
    count: number
  } | number
  isActive: boolean
  deliveryInfo?: {
    fee: number
    minimumOrder: number
    estimatedTime: string
    radius: number
  }
  deliveryTime?: string
  deliveryFee?: number
  minimumOrder?: number
  operatingHours: {
    [key: string]: { open: string; close: string; isOpen: boolean }
  }
  totalOrders: number
  totalRevenue: number
  completionRate: number
  createdAt: string
}

const RestaurantsPage: React.FC = () => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterCategory, setFilterCategory] = useState("all")
  const [filterStatus, setFilterStatus] = useState("all")
  const [showModal, setShowModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // New state for view mode and selection
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const categories = ["Fast food", "Pizza", "Asian", "Middle-eastern", "Desserts", "Healthy", "Coffee"]

  useEffect(() => {
    fetchRestaurants()
  }, [currentPage, searchTerm, filterCategory, filterStatus])

  const fetchRestaurants = async () => {
    try {
      setLoading(true)
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        category: filterCategory === "all" ? "" : filterCategory,
        status: filterStatus === "all" ? "" : filterStatus,
      }
      const response = await restaurantAPI.getRestaurants(params)
      setRestaurants(response.data.restaurants)
      setTotalPages(response.data.totalPages)
    } catch (error) {
      console.error("Error fetching restaurants:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateRestaurant = async (restaurantData: any) => {
    try {
      await restaurantAPI.createRestaurant(restaurantData)
      fetchRestaurants()
      setShowModal(false)
    } catch (error) {
      console.error("Error creating restaurant:", error)
    }
  }

  const handleUpdateRestaurant = async (id: string, restaurantData: any) => {
    try {
      await restaurantAPI.updateRestaurant(id, restaurantData)
      fetchRestaurants()
      setShowModal(false)
      setSelectedRestaurant(null)
    } catch (error) {
      console.error("Error updating restaurant:", error)
    }
  }

  const handleDeleteRestaurant = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this restaurant?")) {
      try {
        await restaurantAPI.deleteRestaurant(id)
        fetchRestaurants()
      } catch (error) {
        console.error("Error deleting restaurant:", error)
      }
    }
  }

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    try {
      await restaurantAPI.updateRestaurant(id, { isActive: !isActive })
      fetchRestaurants()
    } catch (error) {
      console.error("Error updating restaurant status:", error)
    }
  }

  // Define filteredRestaurants BEFORE using it in handlers
  const filteredRestaurants = restaurants.filter((restaurant) => {
    const matchesSearch =
      restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      restaurant.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      restaurant.address.street.toLowerCase().includes(searchTerm.toLowerCase()) ||
      restaurant.address.city.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = filterCategory === "all" || !filterCategory || restaurant.category === filterCategory
    const matchesStatus =
      filterStatus === "all" ||
      !filterStatus ||
      (filterStatus === "active" && restaurant.isActive) ||
      (filterStatus === "inactive" && !restaurant.isActive)

    return matchesSearch && matchesCategory && matchesStatus
  })

  // Bulk Action Handlers
  const handleSelectAll = () => {
    if (selectedIds.length === filteredRestaurants.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(filteredRestaurants.map(r => r._id))
    }
  }

  const handleSelectOne = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const handleBulkAction = async (action: 'activate' | 'deactivate' | 'delete') => {
    if (!window.confirm(`Are you sure you want to ${action} ${selectedIds.length} restaurants?`)) return

    // Mock bulk action loop since API might not support bulk endpoint yet
    for (const id of selectedIds) {
      try {
        if (action === 'delete') {
          await restaurantAPI.deleteRestaurant(id)
        } else {
          await restaurantAPI.updateRestaurant(id, { isActive: action === 'activate' })
        }
      } catch (e) {
        console.error(`Failed to ${action} restaurant ${id}`, e)
      }
    }
    fetchRestaurants()
    setSelectedIds([])
  }

  if (loading) {
    return <LoadingSpinner size="3" text="Loading restaurants..." />
  }

  return (
    <Flex direction="column" gap="6">
      <Flex justify="between" align="center" wrap="wrap" gap="4">
        <PageHeader
          title="Restaurants"
          subtitle="Manage restaurant listings and operations"
        />
        <Flex gap="3">
          <Flex align="center" gap="1" style={{ backgroundColor: 'var(--gray-3)', padding: '4px', borderRadius: 'var(--radius-3)' }}>
            <IconButton
              variant={viewMode === 'grid' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('grid')}
              size="2"
            >
              <LayoutGrid className="w-4 h-4" />
            </IconButton>
            <IconButton
              variant={viewMode === 'list' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('list')}
              size="2"
            >
              <List className="w-4 h-4" />
            </IconButton>
          </Flex>
          <Button
            onClick={() => {
              setSelectedRestaurant(null)
              setShowModal(true)
            }}
          >
            <Flex align="center" gap="2">
              <Plus className="w-4 h-4" />
              Add Restaurant
            </Flex>
          </Button>
        </Flex>
      </Flex>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <Card style={{ backgroundColor: 'var(--accent-3)', borderColor: 'var(--accent-6)' }}>
          <Flex justify="between" align="center">
            <Text weight="bold" color="blue">{selectedIds.length} selected</Text>
            <Flex gap="3">
              <Button size="1" color="green" variant="soft" onClick={() => handleBulkAction('activate')}>Activate</Button>
              <Button size="1" color="red" variant="soft" onClick={() => handleBulkAction('deactivate')}>Deactivate</Button>
              <Button size="1" color="red" variant="outline" onClick={() => handleBulkAction('delete')}>Delete</Button>
            </Flex>
          </Flex>
        </Card>
      )}

      {/* Filters (Enhanced) */}
      <Card>
        <Grid columns={{ initial: "1", md: "4" }} gap="4">
          <Box style={{ position: "relative" }}>
            <Search className="w-4 h-4" style={{ position: "absolute", left: 12, top: 10, color: "var(--gray-9)" }} />
            <TextField.Root
              placeholder="Search restaurants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: 36 }}
            />
          </Box>

          <Select.Root value={filterCategory} onValueChange={setFilterCategory}>
            <Select.Trigger placeholder="All Categories" style={{ width: "100%" }} />
            <Select.Content>
              <Select.Item value="all">All Categories</Select.Item>
              {categories.map((category) => (
                <Select.Item key={category} value={category}>
                  {category}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>

          <Select.Root value={filterStatus} onValueChange={setFilterStatus}>
            <Select.Trigger placeholder="All Status" style={{ width: "100%" }} />
            <Select.Content>
              <Select.Item value="all">All Status</Select.Item>
              <Select.Item value="active">Active</Select.Item>
              <Select.Item value="inactive">Inactive</Select.Item>
            </Select.Content>
          </Select.Root>

          <Button
            variant="soft"
            color="gray"
            onClick={() => {
              setSearchTerm("")
              setFilterCategory("")
              setFilterStatus("")
            }}
          >
            Clear Filters
          </Button>
        </Grid>
      </Card>

      {viewMode === 'grid' ? (
        <Grid columns={{ initial: "1", md: "2", lg: "3" }} gap="6">
          {filteredRestaurants.map((restaurant) => (
            <Card key={restaurant._id} style={{ position: 'relative' }}>
              <Box style={{ position: 'absolute', top: 12, left: 12, zIndex: 10 }}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(restaurant._id)}
                  onChange={() => handleSelectOne(restaurant._id)}
                  className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
              </Box>
              <Flex direction="column" gap="3">
                {/* Image */}
                <Box style={{ position: "relative" }}>
                  <img
                    src={restaurant.image || "/placeholder-restaurant.jpg"}
                    alt={restaurant.name}
                    style={{
                      width: "100%",
                      height: 192,
                      objectFit: "cover",
                      borderRadius: "var(--radius-3)"
                    }}
                  />
                  <Box style={{ position: "absolute", top: 8, right: 8 }}>
                    <Badge color={restaurant.isActive ? "green" : "red"}>
                      {restaurant.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </Box>
                </Box>

                {/* Header */}
                <Flex justify="between" align="start">
                  <Heading size="4">{restaurant.name}</Heading>
                  <Flex align="center" gap="1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" style={{ color: "#facc15" }} />
                    <Text size="2" color="gray">
                      {typeof restaurant.rating === 'object' && (restaurant.rating as any)?.average
                        ? `${(restaurant.rating as any).average.toFixed(1)} (${(restaurant.rating as any).count || 0})`
                        : typeof restaurant.rating === 'number'
                          ? restaurant.rating.toFixed(1)
                          : '0.0'
                      }
                    </Text>
                  </Flex>
                </Flex>

                {/* Category */}
                <Text size="2" color="gray">{restaurant.category}</Text>

                {/* Delivery Info */}
                <Flex gap="4">
                  <Flex align="center" gap="1">
                    <Clock className="w-4 h-4" />
                    <Text size="2" color="gray">
                      {restaurant.deliveryTime || restaurant.deliveryInfo?.estimatedTime || 'N/A'}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="1">
                    <MapPin className="w-4 h-4" />
                    <Text size="2" color="gray">SDG {restaurant.deliveryFee || restaurant.deliveryInfo?.fee || '0'}
                    </Text>
                  </Flex>
                </Flex>

                {/* Stats */}
                <Grid columns="3" gap="2" style={{ textAlign: "center" }}>
                  <Box>
                    <Text size="2" weight="bold">{restaurant.totalOrders}</Text>
                    <Text size="1" color="gray">Orders</Text>
                  </Box>
                  <Box>
                    <Text size="2" weight="bold">SDG {restaurant.totalRevenue}</Text>
                    <Text size="1" color="gray">Revenue</Text>
                  </Box>
                  <Box>
                    <Text size="2" weight="bold">{restaurant.completionRate}%</Text>
                    <Text size="1" color="gray">Success</Text>
                  </Box>
                </Grid>

                {/* Actions */}
                <Flex justify="between" align="center" pt="2" style={{ borderTop: "1px solid var(--gray-5)" }}>
                  <Button
                    size="1"
                    variant="soft"
                    color={restaurant.isActive ? "red" : "green"}
                    onClick={() => handleToggleStatus(restaurant._id, restaurant.isActive)}
                  >
                    {restaurant.isActive ? "Deactivate" : "Activate"}
                  </Button>

                  <Flex gap="2">
                    <IconButton
                      size="2"
                      variant="soft"
                      color="blue"
                      onClick={() => {
                        setSelectedRestaurant(restaurant)
                        setShowDetailsModal(true)
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </IconButton>
                    <IconButton
                      size="2"
                      variant="soft"
                      color="green"
                      onClick={() => {
                        setSelectedRestaurant(restaurant)
                        setShowModal(true)
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </IconButton>
                    <IconButton
                      size="2"
                      variant="soft"
                      color="red"
                      onClick={() => handleDeleteRestaurant(restaurant._id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </IconButton>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          ))}
        </Grid>
      ) : (
        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === filteredRestaurants.length && filteredRestaurants.length > 0}
                    onChange={handleSelectAll}
                  />
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Category</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Rating</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Location</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredRestaurants.map((restaurant) => (
                <Table.Row key={restaurant._id}>
                  <Table.Cell>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(restaurant._id)}
                      onChange={() => handleSelectOne(restaurant._id)}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Flex align="center" gap="2">
                      <Avatar src={restaurant.image} fallback={restaurant.name[0]} size="1" radius="full" />
                      <Text weight="medium">{restaurant.name}</Text>
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>{restaurant.category}</Table.Cell>
                  <Table.Cell>
                    <Badge color={restaurant.isActive ? "green" : "red"}>{restaurant.isActive ? "Active" : "Inactive"}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Flex align="center" gap="1">
                      <Star className="w-3 h-3 text-yellow-500 fill-current" />
                      <Text>{typeof restaurant.rating === 'object' ? restaurant.rating.average.toFixed(1) : restaurant.rating}</Text>
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>{restaurant.address.city}</Table.Cell>
                  <Table.Cell>
                    <Flex gap="2">
                      <IconButton variant="ghost" onClick={() => { setSelectedRestaurant(restaurant); setShowDetailsModal(true) }}>
                        <Eye className="w-4 h-4" />
                      </IconButton>
                      <IconButton variant="ghost" onClick={() => { setSelectedRestaurant(restaurant); setShowModal(true) }}>
                        <Edit className="w-4 h-4" />
                      </IconButton>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="center">
          <Flex gap="2">
            <Button
              variant="soft"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                variant={currentPage === page ? "solid" : "soft"}
                color={currentPage === page ? "red" : "gray"}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Button>
            ))}

            <Button
              variant="soft"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      )}

      {/* Modals */}
      {showModal && (
        <RestaurantModal
          restaurant={selectedRestaurant}
          onClose={() => {
            setShowModal(false)
            setSelectedRestaurant(null)
          }}
          onSave={
            selectedRestaurant ? (data) => handleUpdateRestaurant(selectedRestaurant._id, data) : handleCreateRestaurant
          }
        />
      )}

      {showDetailsModal && selectedRestaurant && (
        <RestaurantDetailsModal
          restaurant={selectedRestaurant}
          onClose={() => {
            setShowDetailsModal(false)
            setSelectedRestaurant(null)
          }}
        />
      )}
    </Flex>
  )
}

export default RestaurantsPage
